import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Chip from '@mui/material/Chip';
import MuiLink from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import { GridColDef } from '@mui/x-data-grid-pro';

import DataGridTable from '@/shared/components/dataDisplay/DataGridTable/DataGridTable';
import { AppPath } from '@/shared/constants/path';
import TableInstance from '@/shared/constants/tableInstance';
import Payment from '@/shared/types/order/payment.type';

import usePaymentsTable_v2 from './hooks/usePaymentsTable_v2';

type PaymentsTable_v2Props = {
  refreshTableIndex: number;
  onIssueRefund: (payment: Payment) => void;
  onVoidTransaction: (payment: Payment) => void;
};

const PaymentsTable_v2: FC<PaymentsTable_v2Props> = ({
  refreshTableIndex,
  onIssueRefund,
  onVoidTransaction,
}) => {
  const { t } = useTranslation(['bookingsServices', 'common']);
  const navigate = useNavigate();

  const { apiRef, model, table, columns, visibilityModel, setVisibilityModel } =
    usePaymentsTable_v2({ refreshTableIndex });

  const customizedColumns: GridColDef[] = columns.map((column) => {
    switch (column.field) {
      case 'in_transaction_id':
        return {
          ...column,
          renderCell(params) {
            const row = params.row as Payment;
            return (
              <MuiLink
                component={Link}
                to={`${AppPath.Payments}/${row.in_transaction_id}`}
              >
                {row.in_transaction_id.split('-')[0]}
              </MuiLink>
            );
          },
        };
      case 'in_order_id':
        return {
          ...column,
          renderCell(params) {
            const row = params.row as Payment;
            return (
              <MuiLink
                component={Link}
                to={`${AppPath.Orders}/${row.in_order_id}`}
              >
                {row.in_order_id.split('-')[0]}
              </MuiLink>
            );
          },
        };
      case 'status':
        return {
          ...column,
          renderCell: (params) => {
            const { status, amount_refunded } = params.row as Payment;
            return status === 'PARTIAL_REFUND' ? (
              <Tooltip
                title={`${t('payments:transactionHasBeenPartiallyRefunded')}: ${amount_refunded}`}
                placement='top'
                tabIndex={-1}
                arrow
                PopperProps={{
                  sx: { maxWidth: 250 },
                }}
              >
                <Chip
                  label={status}
                  size='small'
                  sx={{ gap: 0.5 }}
                  icon={<InfoOutlinedIcon />}
                />
              </Tooltip>
            ) : (
              <Chip label={status} size='small' />
            );
          },
        };
      case 'transaction_type':
        return {
          ...column,
          renderCell: (params) => {
            const { transaction_type } = params.row as Payment;
            return <Chip label={transaction_type} size='small' />;
          },
        };
      case 'payment_method':
        return {
          ...column,
          renderCell: (params) => {
            const { payment_method } = params.row as Payment;
            return payment_method ? (
              <Chip label={payment_method} size='small' />
            ) : (
              payment_method
            );
          },
        };
      case 'customerName':
        return {
          ...column,
          sortable: false,
          filterable: false,
          renderCell: (params) => {
            const { first_name, last_name } = params.row as Payment;
            return `${first_name} ${last_name}`;
          },
        };
      default:
        return column;
    }
  });

  return (
    <div style={{ height: '80vh', width: '100%' }}>
      <DataGridTable
        getRowId={(item) => item.in_transaction_id as string}
        columns={customizedColumns}
        rows={table.data}
        rowCount={table.totalRows}
        loading={table.isLoading}
        columnVisibilityModel={visibilityModel}
        onColumnVisibilityModelChange={setVisibilityModel}
        checkboxSelection={false}
        customProps={{
          tableInstance: TableInstance.Payments,
          apiRef,
          model,
          toolbar: {
            rowActions: [
              {
                type: 'default',
                button: {
                  label: t('payments:viewDetails'),
                  divider: true,
                  onClick: (_, rowId) => {
                    navigate(AppPath.Payments + `/${rowId}`);
                  },
                },
              },
              {
                type: 'default',
                button: {
                  label: t('payments:issueRefund'),
                  onClick: (row, rowId) => {
                    const paymentItem = row.current.getRow(
                      rowId as string,
                    ) as Payment;
                    onIssueRefund(paymentItem);
                  },
                },
              },
              {
                type: 'default',
                button: {
                  label: t('payments:voidTransaction'),
                  divider: true,
                  onClick: (row, rowId) => {
                    const paymentItem = row.current.getRow(
                      rowId as string,
                    ) as Payment;
                    onVoidTransaction(paymentItem);
                  },
                },
              },
            ],
          },
        }}
      />
    </div>
  );
};

export default PaymentsTable_v2;
